<template>
    <div class="launch-wrap">
        <!-- Hero bg -->
        <div class="hero-bg">
            <img v-if="heroSetting" :src="heroSetting.image" :alt="heroSetting.name" class="hero-img" />
            <div v-else class="hero-gradient" />
            <div class="hero-overlay" />
        </div>

        <!-- Title preview flash -->
        <Transition name="fade">
            <div v-if="showPreview" class="preview-flash">
                <div class="preview-inner">
                    <img v-if="heroSetting" :src="heroSetting.image" :alt="heroSetting.name" class="hero-img" style="opacity:0.4" />
                    <div class="hero-overlay" />
                    <div class="preview-content">
                        <span class="preview-eyebrow">Your pitch:</span>
                        <h1 class="preview-title">{{ data.title }}</h1>
                    </div>
                </div>
            </div>
        </Transition>

        <!-- Scrollable content -->
        <div ref="scrollEl" class="launch-scroll hide-scrollbar">
            <div class="launch-center">
                <!-- 5★ banner - only show when user has less than 5 stars -->
                <div v-if="showStarsBanner" class="stars-banner">
                    <img src="https://sm.naughtycdn.com/images/landers/prize/NA-Star.png" alt="Star" style="width:16px;height:16px" /> You have <strong>enough Stars</strong> to launch — no payment needed.
                </div>
                <!-- Social proof -->
                <div class="social-proof">
                    <Users style="width:14px;height:14px" /> {{ formattedStats }}
                </div>

                <!-- Title (editable) -->
                <button class="title-edit-btn" @click="openSheet('title')" aria-label="Edit title">
                    <h1 class="launch-title">{{ data.title || 'Untitled' }}</h1>
                    <span class="edit-icon"><Pencil style="width:14px;height:14px" /></span>
                </button>

                <!-- Cast -->
                <div class="cast-row">
                    <button class="edit-pencil" @click="openSheet('cast')" aria-label="Edit cast"><Pencil style="width:14px;height:14px" /></button>
                    <div v-if="allCast.length > 0" class="cast-avatars">
                        <div v-for="name in allCast" :key="name" class="cast-avatar">
                            <img v-if="photoMap[name]" :src="photoMap[name]" :alt="name" class="cast-img" />
                            <div v-else class="cast-initials">{{ initials(name) }}</div>
                            <span class="cast-name">{{ name }}</span>
                        </div>
                    </div>
                    <button v-else class="add-link" @click="openSheet('cast')">+ Add cast</button>
                </div>

                <!-- Format + settings pills -->
                <div class="meta-pills">
                    <button v-if="data.format" class="meta-pill" @click="openSheet('format')">
                        {{ formatLabels[data.format] }} <Pencil style="width:14px;height:14px" />
                    </button>
                    <button v-else class="meta-pill meta-dashed" @click="openSheet('format')">+ Add format</button>
                    <button v-for="s in data.settings" :key="s" class="meta-pill" @click="openSheet('settings')">
                        {{ getSettingLabel(s) }} <Pencil style="width:14px;height:14px" />
                    </button>
                    <button v-if="data.settings.length === 0" class="meta-pill meta-dashed" @click="openSheet('settings')">+ Add setting</button>
                </div>

                <!-- Tags -->
                <div class="tags-row">
                    <button v-if="data.tags.length > 0" class="tags-edit" @click="openSheet('tags')">
                        <span v-for="tag in data.tags" :key="tag" class="tag-badge"><Hash style="width:10px;height:10px" /> {{ tag }}</span>
                        <span class="edit-icon"><Pencil style="width:14px;height:14px" /></span>
                    </button>
                    <button v-else class="add-link" @click="openSheet('tags')">+ Add tags</button>
                </div>
            </div>

            <!-- Setup + notes -->
            <div class="cards-area">
                <div v-if="data.setup" class="info-card">
                    <button class="card-edit" @click="$emit('editStep', 4)" aria-label="Edit setup"><Pencil style="width:14px;height:14px" /></button>
                    <h3 class="card-label">The Setup</h3>
                    <p class="card-body">{{ data.setup }}</p>
                </div>
                <button v-else class="add-card" @click="$emit('editStep', 4)">+ Add your pitch synopsis</button>
                <div v-if="data.directorsNotes" class="info-card">
                    <button class="card-edit" @click="$emit('editStep', 4)" aria-label="Edit director's notes"><Pencil style="width:14px;height:14px" /></button>
                    <h3 class="card-label">Director's Notes</h3>
                    <p class="card-body notes-body-text">{{ data.directorsNotes }}</p>
                </div>
            </div>
            <div style="height:24px" />
        </div>

        <!-- Footer -->
        <div class="launch-footer">
            <!-- Terms -->
            <label class="terms-label">
                <input type="checkbox" :checked="data.agreedToTerms" class="sr-only" @change="$emit('update:agreedToTerms', $event.target.checked)" />
                <span class="checkbox" :class="{ checked: data.agreedToTerms, nudge: termsNudge && !data.agreedToTerms }">
                    <span v-if="data.agreedToTerms" class="check-mark"><Check style="width:12px;height:12px" /></span>
                </span>
                <span class="terms-text">
                    By submitting, I agree to the
                    <a @click.stop href="https://www.naughtyamerica.com/terms-of-service.html" target="_blank" rel="noopener" class="terms-link">Terms of Service</a>
                    and
                    <a @click.stop href="https://www.naughtyamerica.com/privacy-policy.html" target="_blank" rel="noopener" class="terms-link">Privacy Policy</a>.
                </span>
            </label>

            <!-- Nudge -->
            <Transition name="fade">
                <div v-if="termsNudge && !data.agreedToTerms" class="terms-nudge">
                    <ArrowUp style="width:14px;height:14px" /> Check the box above to launch your pitch.
                </div>
            </Transition>

            <!-- Error message -->
            <Transition name="fade">
                <div v-if="errorMessage" class="error-msg">{{ errorMessage }}</div>
            </Transition>

            <!-- Similar pitch warning -->
            <Transition name="fade">
                <div v-if="similarCasting" class="similar-warn">
                    <div class="similar-head">
                        <div class="pulse-dot" />
                        <p class="similar-title">Similar pitch already active</p>
                    </div>
                    <p class="similar-body">
                        "<span class="similar-quoted">{{ similarCasting.title }}</span>" is already open and accepting support. Are you sure you want to submit a new one?
                    </p>
                    <div class="similar-btns">
                        <button class="similar-btn" @click="$emit('dismissSimilar')">Edit</button>
                        <button class="similar-btn amber" :disabled="loading" @click="$emit('submit', true)">Submit Anyway</button>
                    </div>
                </div>
            </Transition>

            <button v-if="!similarCasting" class="launch-btn" :disabled="loading" @click="handleLaunch">
                <span v-if="loading" class="launch-spinner" />
                <span v-else>LAUNCH PITCH</span>
            </button>
        </div>

        <!-- ── Edit sheets ── -->
        <Teleport to="body">
            <Transition name="backdrop">
                <div v-if="activeSheet" class="sheet-backdrop" @click.self="activeSheet = null">
                    <Transition name="sheet-up">
                        <div v-if="activeSheet" class="bottom-sheet">
                            <div class="drag-handle" />

                            <!-- Title sheet -->
                            <template v-if="activeSheet === 'title'">
                                <div class="sheet-header">
                                    <div><h2 class="sheet-title">Edit title</h2><p class="sheet-sub">{{ titleDraft.length }}/60</p></div>
                                    <button class="sheet-done" @click="activeSheet = null">Done</button>
                                </div>
                                <div class="sheet-pad">
                                    <input v-model="titleDraft" type="text" maxlength="60" placeholder="Type your title…" class="sheet-input" @keydown.enter="saveTitle" />
                                </div>
                                <div class="sheet-pad">
                                    <button class="save-btn" :disabled="!titleDraft.trim()" @click="saveTitle">Save title</button>
                                </div>
                            </template>

                            <!-- Cast sheet -->
                            <template v-else-if="activeSheet === 'cast'">
                                <div class="sheet-header">
                                    <div><h2 class="sheet-title">Edit cast</h2><p class="sheet-sub">{{ allCast.length }}/4 selected</p></div>
                                    <button class="sheet-done" @click="activeSheet = null">Done</button>
                                </div>
                                <div class="sheet-pad"><input v-model="castSearch" type="text" placeholder="Search creators…" class="sheet-input" /></div>
                                <div v-if="allCast.length > 0" class="sheet-chips">
                                    <div v-for="name in data.selectedCast" :key="name" class="chip">
                                        <span>{{ name }}</span>
                                        <button class="chip-remove" @click="$emit('toggleCast', name)"><X style="width:12px;height:12px" /></button>
                                    </div>
                                    <div v-for="name in data.customCast" :key="'c-'+name" class="chip chip-custom">
                                        <span>{{ name }}</span>
                                        <button class="chip-remove" @click="$emit('removeCustomCast', name)"><X style="width:12px;height:12px" /></button>
                                    </div>
                                </div>
                                <div class="sheet-scroll hide-scrollbar">
                                    <div v-if="showCastZeroState" class="sheet-pad">
                                        <button class="custom-add-btn" @click="addCustomCastFromSearch"><Plus style="width:14px;height:14px" /> Add "{{ castSearch.trim() }}" as custom cast</button>
                                    </div>
                                    <div class="cast-sheet-grid">
                                        <button
                                            v-for="c in filteredSheetCast"
                                            :key="c.id"
                                            class="cast-sheet-item"
                                            :disabled="!data.selectedCast.includes(c.name) && allCast.length >= 4"
                                            @click="$emit('toggleCast', c.name)"
                                        >
                                            <div class="cast-sheet-avatar" :class="{ 'selected-ring': data.selectedCast.includes(c.name) }">
                                                <img :src="c.photo" :alt="c.name" class="avatar-img" />
                                            </div>
                                            <span class="cast-sheet-name">{{ c.name }}</span>
                                        </button>
                                    </div>
                                </div>
                            </template>

                            <!-- Format sheet -->
                            <template v-else-if="activeSheet === 'format'">
                                <div class="sheet-header">
                                    <h2 class="sheet-title">Change format</h2>
                                    <button class="sheet-done" @click="activeSheet = null">Done</button>
                                </div>
                                <div class="sheet-scroll hide-scrollbar">
                                    <div class="format-sheet-grid">
                                        <button
                                            v-for="f in FORMATS"
                                            :key="f.id"
                                            class="format-sheet-card"
                                            :class="{ 'is-selected': data.format === f.id }"
                                            @click="() => { $emit('update:format', f.id); activeSheet = null }"
                                        >
                                            <div class="format-preview-mini">
                                                <div class="format-grid-bg-mini" />
                                                <div v-if="data.format === f.id" class="format-glow-mini" />

                                                <div v-if="f.layered" class="format-layered-mini" :style="{ width: f.widthCss, aspectRatio: f.ratio }">
                                                    <div :class="['layer-back-mini', data.format === f.id ? 'is-selected' : '']" />
                                                    <div :class="['layer-front-mini', data.format === f.id ? 'is-selected' : '']" />
                                                    <component :is="formatIconMap[f.id]" :class="['layer-icon-mini', data.format === f.id ? 'is-selected' : '']" />
                                                </div>
                                                <div v-else :class="['format-solid-mini', data.format === f.id ? 'is-selected' : '']" :style="{ width: f.widthCss, aspectRatio: f.ratio }">
                                                    <component :is="formatIconMap[f.id]" />
                                                </div>
                                            </div>

                                            <div>
                                                <div class="format-row-mini">
                                                    <p class="format-label-mini">{{ f.name }}</p>
                                                    <p :class="['format-sub-mini', data.format === f.id ? 'is-selected' : '']">{{ f.subtitle }}</p>
                                                </div>
                                                <p :class="['format-desc-mini', data.format === f.id ? 'is-selected' : '']">{{ f.description }}</p>
                                            </div>

                                            <Transition name="check-pop">
                                                <div v-if="data.format === f.id" class="format-check-mini">
                                                    <Check class="icon-xs" stroke-width="3" style="color:#0a0612" />
                                                </div>
                                            </Transition>
                                        </button>
                                    </div>
                                </div>
                            </template>

                            <!-- Settings sheet -->
                            <template v-else-if="activeSheet === 'settings'">
                                <div class="sheet-header">
                                    <div><h2 class="sheet-title">Edit settings</h2><p class="sheet-sub">Pick one or more locations</p></div>
                                    <button class="sheet-done" @click="activeSheet = null">Done</button>
                                </div>
                                <div v-if="data.settings.length > 0" class="sheet-chips">
                                    <div v-for="id in data.settings" :key="id" class="chip">
                                        <span>{{ getSettingLabel(id) }}</span>
                                        <button class="chip-remove" @click="$emit('removeSetting', id)"><X style="width:12px;height:12px" /></button>
                                    </div>
                                </div>
                                <div class="sheet-scroll hide-scrollbar">
                                    <div class="setting-sheet-grid">
                                        <button
                                            v-for="s in SETTINGS_DATA"
                                            :key="s.id"
                                            class="setting-sheet-card"
                                            :class="{ 'setting-active': data.settings.includes(s.id) }"
                                            @click="$emit('toggleSetting', s.id)"
                                        >
                                            <img :src="s.image" :alt="s.name" class="setting-sheet-img" />
                                            <div class="setting-overlay" />
                                            <div class="setting-card-label">{{ s.name }}</div>
                                        </button>
                                    </div>
                                </div>
                            </template>

                            <!-- Tags sheet -->
                            <template v-else-if="activeSheet === 'tags'">
                                <div class="sheet-header">
                                    <div><h2 class="sheet-title">Edit tags</h2><p class="sheet-sub">{{ data.tags.length }}/10</p></div>
                                    <button class="sheet-done" @click="activeSheet = null">Done</button>
                                </div>
                                <div v-if="data.tags.length > 0" class="sheet-chips">
                                    <div v-for="tag in data.tags" :key="tag" class="chip chip-tag">
                                        <span><Hash style="width:10px;height:10px" /> {{ tag }}</span>
                                        <button class="chip-remove" @click="$emit('toggleTag', tag)"><X style="width:12px;height:12px" /></button>
                                    </div>
                                </div>
                                <div class="sheet-pad">
                                    <input v-model="tagSheetSearch" type="text" placeholder="Search or create a tag…" class="sheet-input" :disabled="data.tags.length >= 10" @keydown.enter="onTagSheetEnter" />
                                </div>
                                <div class="sheet-scroll hide-scrollbar">
                                    <p v-if="!tagSheetSearch.trim()" class="sheet-hint">Start typing to search 100+ tags, or create your own.</p>
                                    <button v-for="tag in filteredSheetTags" :key="tag" class="sheet-tag-btn" @click="sheetAddTag(tag)">
                                        <Hash style="width:10px;height:10px" /> {{ tag }}
                                    </button>
                                    <button v-if="!exactSheetTagMatch && tagSheetSearch.trim()" class="sheet-custom-tag-btn" @click="sheetAddCustomTag">
                                        + Add "{{ tagSheetSearch.trim().slice(0, 30) }}" as new tag
                                    </button>
                                </div>
                            </template>

                        </div>
                    </Transition>
                </div>
            </Transition>
        </Teleport>
    </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted, h } from 'vue'
import { usePage } from '@inertiajs/vue3'
import { Users, Film, Pencil, Plus, X, Check, ArrowUp, Smartphone, Layers, Hash } from 'lucide-vue-next'

const Monitor = {
    render: () => h('svg', { viewBox: '0 0 24 24', fill: 'none', xmlns: 'http://www.w3.org/2000/svg', stroke: 'currentColor', 'stroke-width': '2', 'stroke-linecap': 'round', 'stroke-linejoin': 'round' }, [
        h('rect', { width: '20', height: '14', x: '2', y: '3', rx: '2' }),
        h('line', { x1: '8', x2: '16', y1: '21', y2: '21' }),
        h('line', { x1: '12', x2: '12', y1: '17', y2: '21' })
    ])
}

const VrHeadsetIcon = {
    render: () => h('svg', { viewBox: '0 0 15 15', fill: 'none', xmlns: 'http://www.w3.org/2000/svg' }, [
        h('path', { d: 'M0.851071 5.39969L0.988431 5.88046H0.988431L0.851071 5.39969ZM14.1489 5.39969L14.2863 4.91893L14.1489 5.39969ZM4.58046 12.3525L5.0194 12.5919L5.0194 12.5919L4.58046 12.3525ZM10.4195 12.3525L10.8585 12.1131L10.8585 12.1131L10.4195 12.3525ZM2.99711 3.75722L2.53287 3.57152L2.99711 3.75722ZM2.03576 4.8143C1.93321 5.0707 2.05791 5.36168 2.3143 5.46424C2.5707 5.5668 2.86168 5.44209 2.96424 5.1857L2.03576 4.8143ZM12.0029 3.75722L12.4671 3.57152V3.57152L12.0029 3.75722ZM12.0358 5.1857C12.1383 5.44209 12.4293 5.5668 12.6857 5.46424C12.9421 5.36168 13.0668 5.0707 12.9642 4.8143L12.0358 5.1857ZM14 5.86512V9.21017H15V5.86512H14ZM11.2102 12H10.668V13H11.2102V12ZM4.33198 12H3.78983V13H4.33198V12ZM1 9.21017V5.86512H0V9.21017H1ZM0.988431 5.88046C5.24432 4.66449 9.75567 4.66449 14.0116 5.88046L14.2863 4.91893C9.85084 3.65166 5.14916 3.65166 0.71371 4.91893L0.988431 5.88046ZM3.78983 12C2.24905 12 1 10.7509 1 9.21017H0C0 11.3032 1.69677 13 3.78983 13V12ZM4.14151 12.1131C4.17953 12.0434 4.25259 12 4.33198 12V13C4.61853 13 4.88219 12.8435 5.0194 12.5919L4.14151 12.1131ZM5.0194 12.5919C6.09036 10.6285 8.90964 10.6285 9.9806 12.5919L10.8585 12.1131C9.40852 9.45479 5.59148 9.45479 4.14151 12.1131L5.0194 12.5919ZM10.668 12C10.7474 12 10.8205 12.0434 10.8585 12.1131L9.9806 12.5919C10.1178 12.8435 10.3815 13 10.668 13V12ZM14 9.21017C14 10.7509 12.7509 12 11.2102 12V13C13.3032 13 15 11.3032 15 9.21017H14ZM15 5.86512C15 5.42576 14.7087 5.03963 14.2863 4.91893L14.0116 5.88046C14.0047 5.8785 14 5.87224 14 5.86512H15ZM1 5.86512C1 5.87224 0.995279 5.8785 0.988431 5.88046L0.71371 4.91893C0.291256 5.03963 0 5.42576 0 5.86512H1ZM2.53287 3.57152L2.03576 4.8143L2.96424 5.1857L3.46135 3.94291L2.53287 3.57152ZM11.5386 3.94291L12.0358 5.1857L12.9642 4.8143L12.4671 3.57152L11.5386 3.94291ZM4.85407 3H10.1459V2H4.85407V3ZM12.4671 3.57152C12.0875 2.62238 11.1682 2 10.1459 2V3C10.7593 3 11.3109 3.37343 11.5386 3.94291L12.4671 3.57152ZM3.46135 3.94291C3.68915 3.37343 4.24071 3 4.85407 3V2C3.83176 2 2.91254 2.62238 2.53287 3.57152L3.46135 3.94291Z', fill: 'currentColor' })
    ])
}

const formatIconMap = { 'Standard': Monitor, 'VR': VrHeadsetIcon, 'Vertical': Smartphone, 'Mixed Reality': Layers }

const SETTINGS_DATA = [
    { id:'bedroom',   name:'Bedroom',         image:'https://sm.naughtycdn.com/assets/wizard/setting/bedroom.png' },
    { id:'office',    name:'Office',          image:'https://sm.naughtycdn.com/assets/wizard/setting/office.png' },
    { id:'pool',      name:'Pool / Outdoors', image:'https://sm.naughtycdn.com/assets/wizard/setting/pool.png' },
    { id:'hotel',     name:'Hotel Room',      image:'https://sm.naughtycdn.com/assets/wizard/setting/hotel.png' },
    { id:'gym',       name:'Gym',             image:'https://sm.naughtycdn.com/assets/wizard/setting/gym.png' },
    { id:'kitchen',   name:'Kitchen',         image:'https://sm.naughtycdn.com/assets/wizard/setting/kitchen.png' },
    { id:'living',    name:'Living Room',     image:'https://sm.naughtycdn.com/assets/wizard/setting/living_room.png' },
    { id:'classroom', name:'Classroom',       image:'https://sm.naughtycdn.com/assets/wizard/setting/classroom.png' },
    { id:'massage',   name:'Massage Studio',  image:'https://sm.naughtycdn.com/assets/wizard/setting/massage_studio.png' },
]
const FORMATS = [
    { id:'Standard',      name:'Standard',      subtitle:'16 : 9',      description:'Widescreen — TVs and streaming.',  widthCss: '78%', ratio: '16/9' },
    { id:'VR',            name:'VR',            subtitle:'180° / 360°', description:'Fully immersive. Right there.',     widthCss: '90%', ratio: '2/1' },
    { id:'Vertical',      name:'Vertical',      subtitle:'9 : 16',      description:'Phone-native, portrait.',           widthCss: '42%', ratio: '9/16' },
    { id:'Mixed Reality', name:'Mixed Reality', subtitle:'Immersive',   description:'Spatial — Vision Pro, Quest.',      widthCss: '68%', ratio: '4/3', layered: true },
]

const props = defineProps({
    data: Object,
    performers: { type: Array, default: () => [] },
    castingStats: { type: Object, default: () => ({ total_pitches: 0, cast_this_year: 0 }) },
    allTags: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
    similarCasting: { type: Object, default: null },
    errorMessage: { type: String, default: '' },
    userPitchesCount: { type: Number, default: 0 },
})

const { props: sharedProps } = usePage()
const userStarBalance = computed(() => sharedProps.auth?.user?.wallet_balance ?? 0)
const showStarsBanner = computed(() => userStarBalance.value >= 5 && props.userPitchesCount === 0)

const formattedStats = computed(() => {
    const totalPitches = props.castingStats.total_pitches.toLocaleString()
    const castThisYear = props.castingStats.cast_this_year.toLocaleString()
    // return `${totalPitches} pitches submitted · ${castThisYear} cast this year`
    return `${totalPitches} pitches submitted`
})
const emit = defineEmits([
    'update:agreedToTerms', 'editStep', 'submit', 'dismissSimilar',
    'update:title', 'toggleCast', 'addCustomCast', 'removeCustomCast',
    'update:format', 'toggleSetting', 'addCustomSetting', 'removeSetting', 'toggleTag',
])

const scrollEl    = ref(null)
const showPreview = ref(false) // Disabled to prevent freeze on step entry
const termsNudge  = ref(false)
const activeSheet = ref(null)
const titleDraft  = ref(props.data.title)
const castSearch  = ref('')
const tagSheetSearch = ref('')

// Preview flash disabled - was causing freeze when entering final step
// let previewTimer = null
// onMounted(() => { previewTimer = setTimeout(() => { showPreview.value = false }, 800) })
// onUnmounted(() => clearTimeout(previewTimer))
watch(activeSheet, v => { document.body.style.overflow = v ? 'hidden' : '' })
onUnmounted(() => { document.body.style.overflow = '' })

watch(() => props.data.agreedToTerms, v => { if (v) termsNudge.value = false })

const allCast = computed(() => [...props.data.selectedCast, ...props.data.customCast])
const formatLabels = { 'Standard':'STANDARD · SIDE VIEW', 'VR':'VR · FIRST-PERSON', 'Vertical':'VERTICAL · SHORTS', 'Mixed Reality':'IMMERSIVE · MR' }
const heroSetting = computed(() => {
    const firstPreset = props.data.settings.find(s => SETTINGS_DATA.some(sd => sd.id === s))
    return firstPreset ? SETTINGS_DATA.find(s => s.id === firstPreset) : null
})

function getSettingLabel(id) { return SETTINGS_DATA.find(s => s.id === id)?.name ?? id }
function initials(name) { return name.split(' ').map(w => w[0]).join('').substring(0, 2).toUpperCase() }

function handleLaunch() {
    if (!props.data.agreedToTerms) { termsNudge.value = true; return }
    emit('submit')
}
function openSheet(name) { titleDraft.value = props.data.title; castSearch.value = ''; tagSheetSearch.value = ''; activeSheet.value = name }
function saveTitle() { if (titleDraft.value.trim()) { emit('update:title', titleDraft.value.trim().slice(0, 60)); activeSheet.value = null } }

// Cast sheet - use real performers data
const CREATOR_CATALOG = computed(() =>
    props.performers.map(p => ({ id: String(p.id), name: p.name, photo: p.thumbnail_url }))
)
const photoMap = computed(() => Object.fromEntries(CREATOR_CATALOG.value.map(c => [c.name, c.photo])))

const filteredSheetCast = computed(() =>
    CREATOR_CATALOG.value.filter(c => c.name.toLowerCase().includes(castSearch.value.toLowerCase()))
)
const showCastZeroState = computed(() => {
    const q = castSearch.value.trim()
    return q && filteredSheetCast.value.length === 0 && allCast.value.length < 4 && !allCast.value.some(n => n.toLowerCase() === q.toLowerCase())
})
function addCustomCastFromSearch() {
    const q = castSearch.value.trim()
    if (q) { emit('addCustomCast', q); castSearch.value = '' }
}

// Tag sheet
const filteredSheetTags = computed(() =>
    tagSheetSearch.value.trim()
        ? props.allTags.filter(t => t.toLowerCase().includes(tagSheetSearch.value.toLowerCase()) && !props.data.tags.includes(t)).slice(0, 8)
        : []
)
const exactSheetTagMatch = computed(() =>
    tagSheetSearch.value.trim()
        ? props.allTags.some(t => t.toLowerCase() === tagSheetSearch.value.trim().toLowerCase()) ||
        props.data.tags.some(t => t.toLowerCase() === tagSheetSearch.value.trim().toLowerCase())
        : true
)
function sheetAddTag(tag) { if (props.data.tags.length < 10) { emit('toggleTag', tag); tagSheetSearch.value = '' } }
function sheetAddCustomTag() {
    const val = tagSheetSearch.value.trim().slice(0, 30)
    if (val && props.data.tags.length < 10 && !props.data.tags.some(t => t.toLowerCase() === val.toLowerCase())) emit('toggleTag', val)
    tagSheetSearch.value = ''
}
function onTagSheetEnter() {
    if (!tagSheetSearch.value.trim()) return
    if (filteredSheetTags.value.length > 0) sheetAddTag(filteredSheetTags.value[0])
    else if (!exactSheetTagMatch.value) sheetAddCustomTag()
}
</script>

<style scoped>
.launch-wrap { position:relative; display:flex; flex-direction:column; height:100%; background:#0A0A0A; overflow:hidden; }
.hero-bg { position:absolute; inset:0; z-index:0; }
.hero-img { width:100%; height:100%; object-fit:cover; opacity:0.35; }
.hero-gradient { width:100%; height:100%; background:linear-gradient(135deg,#0A0A0A,rgba(0,82,204,0.2)); }
.hero-overlay { position:absolute; inset:0; background:linear-gradient(to bottom,rgba(10,10,10,0.8),rgba(10,10,10,0.6),rgba(10,10,10,1)); }
.preview-flash { position:absolute; inset:0; z-index:100; background:#0A0A0A; }
.preview-inner { position:relative; width:100%; height:100%; display:flex; align-items:center; justify-content:center; }
.preview-content { position:relative; z-index:10; display:flex; flex-direction:column; align-items:center; text-align:center; padding:24px; }
.preview-eyebrow { color:#4D94FF; font-size:13px; font-weight:700; text-transform:uppercase; letter-spacing:0.1em; margin-bottom:12px; }
.preview-title { font-size:48px; font-weight:900; color:white; text-transform:uppercase; letter-spacing:-0.02em; line-height:1; text-shadow:0 4px 24px rgba(0,0,0,0.8); }
.launch-scroll { position:relative; z-index:10; flex:1; min-height:0; overflow-y:auto; -webkit-overflow-scrolling:touch; }
.launch-center { padding:32px 20px 24px; display:flex; flex-direction:column; align-items:center; text-align:center; }
.stars-banner { width:100%; margin-bottom:16px; background:rgba(245,158,11,0.1); border:1px solid rgba(245,158,11,0.3); border-radius:12px; padding:12px 16px; font-size:13px; color:rgba(245,158,11,0.9); font-weight:500; text-align:left; display:flex; align-items:center; gap:8px; }
.social-proof { font-size:11px; color:rgba(255,255,255,0.5); margin-bottom:24px; display:flex; align-items:center; gap:6px; justify-content:center; }
.title-edit-btn { display:inline-flex; align-items:flex-start; gap:8px; background:none; border:none; cursor:pointer; margin-bottom:24px; max-width:100%; }
.launch-title { font-size:28px; font-weight:900; color:white; text-transform:uppercase; letter-spacing:-0.01em; line-height:1.1; word-break:break-word; text-align:left; }
.edit-icon { color:rgba(255,255,255,0.4); font-size:14px; flex-shrink:0; margin-top:4px; }
.cast-row { position:relative; width:100%; max-width:400px; margin-bottom:24px; }
.edit-pencil { position:absolute; top:-8px; right:0; background:rgba(255,255,255,0.05); border:none; border-radius:50%; width:36px; height:36px; cursor:pointer; color:rgba(255,255,255,0.6); font-size:14px; display:flex; align-items:center; justify-content:center; }
.cast-avatars { display:flex; flex-wrap:wrap; justify-content:center; gap:20px 28px; padding-top:12px; }
.cast-avatar { display:flex; flex-direction:column; align-items:center; gap:6px; }
.cast-img { width:60px; height:60px; border-radius:50%; object-fit:cover; border:2px solid rgba(77,148,255,0.5); }
.cast-initials { width:60px; height:60px; border-radius:50%; background:rgba(0,82,204,0.3); border:2px solid rgba(77,148,255,0.5); display:flex; align-items:center; justify-content:center; color:white; font-weight:700; font-size:16px; }
.cast-name { font-size:12px; color:rgba(255,255,255,0.9); font-weight:500; max-width:70px; text-align:center; }
.add-link { background:none; border:none; color:rgba(255,255,255,0.5); font-size:13px; text-decoration:underline; cursor:pointer; padding-top:8px; min-height:36px; }
.meta-pills { display:flex; flex-wrap:wrap; justify-content:center; gap:8px; margin-bottom:16px; max-width:500px; }
.meta-pill { background:rgba(0,0,0,0.5); border:1px solid rgba(255,255,255,0.1); border-radius:999px; padding:6px 12px; font-size:12px; font-weight:700; color:rgba(255,255,255,0.8); cursor:pointer; letter-spacing:0.02em; min-height:34px; display:flex; align-items:center; gap:6px; }
.meta-dashed { border-style:dashed; color:rgba(255,255,255,0.5); font-weight:400; }
.tags-row { width:100%; display:flex; justify-content:center; margin-bottom:8px; }
.tags-edit { display:flex; flex-wrap:wrap; justify-content:center; gap:8px; background:none; border:none; cursor:pointer; padding:8px; min-height:44px; max-width:500px; }
.tag-badge { background:rgba(255,255,255,0.05); border:1px solid rgba(255,255,255,0.1); border-radius:999px; padding:4px 10px; font-size:11px; color:rgba(255,255,255,0.6); display:flex; align-items:center; gap:4px; }
.cards-area { padding:0 20px 16px; display:flex; flex-direction:column; gap:16px; max-width:600px; margin:0 auto; width:100%; }
.info-card { background:rgba(255,255,255,0.05); border:1px solid rgba(255,255,255,0.1); border-radius:16px; padding:20px; position:relative; }
.card-edit { position:absolute; top:10px; right:10px; background:rgba(255,255,255,0.05); border:none; border-radius:50%; width:36px; height:36px; color:rgba(255,255,255,0.5); cursor:pointer; font-size:13px; display:flex; align-items:center; justify-content:center; }
.card-label { font-size:11px; font-weight:700; color:#4D94FF; text-transform:uppercase; letter-spacing:0.1em; margin-bottom:10px; }
.card-body { color:rgba(255,255,255,0.9); font-size:14px; line-height:1.6; padding-right:40px; }
.notes-body-text { color:rgba(255,255,255,0.7); }
.add-card { background:rgba(255,255,255,0.05); border:1px dashed rgba(255,255,255,0.2); border-radius:16px; padding:16px; color:rgba(255,255,255,0.4); font-size:13px; text-align:center; cursor:pointer; min-height:52px; width:100%; }
.launch-footer { position:relative; z-index:20; background:#0A0A0A; border-top:1px solid rgba(255,255,255,0.1); padding:14px 16px 20px; display:flex; flex-direction:column; gap:10px; padding-bottom:max(20px,env(safe-area-inset-bottom,0px)); }
.terms-label { display:flex; align-items:flex-start; gap:10px; cursor:pointer; min-height:30px; padding-top:4px; user-select:none; }
.sr-only { position:absolute; width:1px; height:1px; padding:0; margin:-1px; overflow:hidden; clip:rect(0,0,0,0); white-space:nowrap; border:0; }
.checkbox { width:20px; height:20px; border-radius:6px; border:2px solid rgba(255,255,255,0.3); background:rgba(255,255,255,0.05); display:flex; align-items:center; justify-content:center; flex-shrink:0; margin-top:2px; transition:all 0.2s; }
.checked { border-color:#0052CC; background:#0052CC; }
.nudge { border-color:rgba(251,191,36,0.8); box-shadow:0 0 0 3px rgba(251,191,36,0.2); }
.check-mark { color:white; font-size:12px; font-weight:700; }
.terms-text { font-size:12px; color:rgba(255,255,255,0.6); line-height:1.5; }
.terms-link { color:#4D94FF; text-decoration:underline; }
.terms-link:hover { color:#6BA3FF; }
.terms-nudge { color:rgba(251,191,36,0.9); font-size:12px; display:flex; align-items:center; gap:6px; padding:8px 12px; background:rgba(245,158,11,0.1); border:1px solid rgba(251,191,36,0.3); border-radius:12px; }
.error-msg { font-size:13px; color:#f87171; background:rgba(239,68,68,0.1); border:1px solid rgba(239,68,68,0.2); border-radius:12px; padding:10px 14px; }
.similar-warn { border-radius:14px; border:1px solid rgba(245,158,11,0.3); background:linear-gradient(to bottom,rgba(245,158,11,0.1),rgba(234,88,12,0.05)); padding:14px; }
.similar-head { display:flex; align-items:center; gap:8px; margin-bottom:6px; }
.pulse-dot { width:8px; height:8px; border-radius:50%; background:#fbbf24; flex-shrink:0; animation:pulse 2s infinite; }
@keyframes pulse { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:0.6;transform:scale(0.85)} }
.similar-title { color:#fcd34d; font-size:12px; font-weight:700; margin:0; }
.similar-body { color:rgba(255,255,255,0.55); font-size:12px; line-height:1.4; margin:0 0 12px; }
.similar-quoted { color:rgba(255,255,255,0.8); }
.similar-btns { display:grid; grid-template-columns:1fr 1fr; gap:6px; }
.similar-btn { padding:8px 0; border-radius:8px; font-size:12px; font-weight:700; background:rgba(255,255,255,0.03); border:1px solid rgba(255,255,255,0.1); color:rgba(255,255,255,0.65); cursor:pointer; transition:all 0.2s; font-family:inherit; }
.similar-btn:hover { background:rgba(255,255,255,0.06); }
.similar-btn.amber { background:#d97706; border-color:#d97706; color:white; }
.similar-btn.amber:hover { background:#f59e0b; }
.similar-btn.amber:disabled { opacity:0.6; cursor:not-allowed; }
.launch-btn { width:100%; background:#0052CC; color:white; font-weight:900; font-size:19px; height:52px; border-radius:16px; border:none; cursor:pointer; letter-spacing:0.02em; box-shadow:0 4px 20px rgba(0,82,204,0.3); transition:opacity 0.2s; display:flex; align-items:center; justify-content:center; }
.launch-btn:hover:not(:disabled) { opacity:0.9; }
.launch-btn:disabled { opacity:0.7; cursor:not-allowed; }
.launch-spinner { width:22px; height:22px; border:3px solid rgba(255,255,255,0.3); border-top-color:white; border-radius:50%; animation:spin 0.7s linear infinite; }
@keyframes spin { to { transform:rotate(360deg); } }
/* Sheets */
.sheet-backdrop { position:fixed; inset:0; z-index:70; background:rgba(0,0,0,0.6); backdrop-filter:blur(4px); display:flex; align-items:flex-end; }
.bottom-sheet { width:100%; max-height:85dvh; display:flex; flex-direction:column; background:#121212; border-top:1px solid rgba(255,255,255,0.1); border-radius:20px 20px 0 0; padding-bottom:max(env(safe-area-inset-bottom,0px),16px); }
.drag-handle { width:36px; height:4px; border-radius:2px; background:rgba(255,255,255,0.2); margin:10px auto 4px; flex-shrink:0; }
.sheet-header { display:flex; align-items:center; justify-content:space-between; padding:10px 20px 14px; flex-shrink:0; }
.sheet-title { font-size:15px; font-weight:700; color:white; }
.sheet-sub { font-size:11px; color:rgba(255,255,255,0.5); margin-top:2px; }
.sheet-done { background:none; border:none; color:#4D94FF; font-weight:700; font-size:13px; cursor:pointer; min-height:44px; padding:0 10px; }
.sheet-pad { padding:0 20px 12px; flex-shrink:0; }
.sheet-input { width:100%; background:rgba(255,255,255,0.05); border:1px solid rgba(255,255,255,0.1); border-radius:12px; padding:10px 14px; color:white; outline:none; font-size:16px !important; font-family:inherit; }
.sheet-input:focus { border-color:rgba(77,148,255,0.5); }
.sheet-input:disabled { opacity:0.5; }
.save-btn { width:100%; background:#0052CC; color:white; font-weight:700; font-size:15px; padding:12px 0; border-radius:16px; border:none; cursor:pointer; }
.save-btn:disabled { opacity:0.4; }
.sheet-chips { display:flex; flex-wrap:wrap; gap:6px; padding:0 20px 12px; flex-shrink:0; }
.chip { display:flex; align-items:center; gap:6px; background:rgba(0,82,204,0.2); border:1px solid rgba(0,82,204,0.3); border-radius:999px; padding:5px 8px 5px 12px; font-size:12px; color:#4D94FF; }
.chip-custom { background:rgba(255,255,255,0.1); border-color:rgba(255,255,255,0.2); color:white; }
.chip-tag { font-size:12px; }
.chip-remove { background:none; border:none; color:inherit; cursor:pointer; padding:3px; border-radius:50%; opacity:0.7; }
.sheet-scroll { flex:1; overflow-y:auto; padding:0 20px 12px; }
.cast-sheet-grid { display:grid; grid-template-columns:repeat(3,1fr); gap:16px 10px; }
.cast-sheet-item { display:flex; flex-direction:column; align-items:center; gap:6px; background:none; border:none; cursor:pointer; padding:0; }
.cast-sheet-item:disabled { opacity:0.3; cursor:not-allowed; }
.cast-sheet-avatar { width:56px; height:56px; border-radius:50%; overflow:hidden; }
.selected-ring { box-shadow:0 0 0 3px #4D94FF; }
.avatar-img { width:100%; height:100%; object-fit:cover; display:block; }
.cast-sheet-name { font-size:11px; color:rgba(255,255,255,0.8); text-align:center; line-height:1.3; word-break:break-word; }
.format-sheet-grid { display:flex; flex-direction:column; gap:12px; padding:0 20px 12px; }
.format-sheet-card { position:relative; display:flex; align-items:center; gap:12px; padding:12px; border-radius:14px; border:2px solid rgba(255,255,255,0.1); background:rgba(255,255,255,0.03); cursor:pointer; transition:all 0.2s; }
.format-sheet-card.is-selected { border-color:#0BDBDB; background:rgba(11,219,219,0.08); }
.format-preview-mini { position:relative; width:80px; height:60px; flex-shrink:0; display:flex; align-items:center; justify-content:center; }
.format-grid-bg-mini { position:absolute; inset:0; background:repeating-linear-gradient(0deg,rgba(255,255,255,0.03) 0px,rgba(255,255,255,0.03) 1px,transparent 1px,transparent 6px),repeating-linear-gradient(90deg,rgba(255,255,255,0.03) 0px,rgba(255,255,255,0.03) 1px,transparent 1px,transparent 6px); border-radius:8px; }
.format-glow-mini { position:absolute; inset:-2px; background:radial-gradient(circle at center,rgba(11,219,219,0.15),transparent 70%); border-radius:10px; pointer-events:none; }
.format-solid-mini { position:relative; background:rgba(255,255,255,0.08); border-radius:6px; display:flex; align-items:center; justify-content:center; color:rgba(255,255,255,0.4); transition:all 0.2s; }
.format-solid-mini.is-selected { background:rgba(11,219,219,0.2); color:#0BDBDB; }
.format-solid-mini svg { width:20px; height:20px; }
.format-layered-mini { position:relative; display:flex; align-items:center; justify-content:center; }
.layer-back-mini { position:absolute; inset:0; background:rgba(255,255,255,0.06); border-radius:6px; transform:translate(-3px,-3px); transition:all 0.2s; }
.layer-back-mini.is-selected { background:rgba(11,219,219,0.15); }
.layer-front-mini { position:absolute; inset:0; background:rgba(255,255,255,0.08); border-radius:6px; transform:translate(3px,3px); transition:all 0.2s; }
.layer-front-mini.is-selected { background:rgba(11,219,219,0.2); }
.layer-icon-mini { position:relative; z-index:1; color:rgba(255,255,255,0.4); width:20px; height:20px; transition:color 0.2s; }
.layer-icon-mini.is-selected { color:#0BDBDB; }
.format-row-mini { display:flex; align-items:baseline; gap:8px; margin-bottom:2px; }
.format-label-mini { font-weight:700; color:white; font-size:14px; line-height:1.2; }
.format-sub-mini { font-size:11px; color:rgba(255,255,255,0.4); transition:color 0.2s; }
.format-sub-mini.is-selected { color:rgba(11,219,219,0.8); }
.format-desc-mini { font-size:11px; color:rgba(255,255,255,0.4); line-height:1.3; transition:color 0.2s; }
.format-desc-mini.is-selected { color:rgba(255,255,255,0.6); }
.format-check-mini { position:absolute; top:10px; right:10px; width:20px; height:20px; border-radius:50%; background:#0BDBDB; display:flex; align-items:center; justify-content:center; }
.icon-xs { width:12px; height:12px; }
.check-pop-enter-active,.check-pop-leave-active { transition:all 0.2s; }
.check-pop-enter-from,.check-pop-leave-to { opacity:0; transform:scale(0.5); }
.check-pop-enter-to,.check-pop-leave-from { opacity:1; transform:scale(1); }
.setting-sheet-grid { display:grid; grid-template-columns:1fr 1fr; gap:10px; }
.setting-sheet-card { position:relative; aspect-ratio:4/3; border-radius:12px; overflow:hidden; border:2px solid transparent; cursor:pointer; padding:0; }
.setting-active { border-color:#4D94FF; }
.setting-sheet-img { position:absolute; inset:0; width:100%; height:100%; object-fit:cover; }
.setting-overlay { position:absolute; inset:0; background:linear-gradient(to top,rgba(0,0,0,0.65),transparent,transparent); }
.setting-card-label { position:absolute; bottom:0; left:0; padding:8px 10px; font-weight:700; color:white; font-size:12px; }
.custom-add-btn { width:100%; display:flex; align-items:center; gap:8px; background:rgba(0,82,204,0.15); border:1px solid rgba(0,82,204,0.4); border-radius:12px; padding:10px 14px; font-size:13px; color:#4D94FF; cursor:pointer; min-height:44px; }
.sheet-hint { font-size:12px; color:rgba(255,255,255,0.4); padding:12px 0; }
.sheet-tag-btn { width:100%; display:flex; align-items:center; padding:10px; font-size:13px; color:rgba(255,255,255,0.8); background:none; border:none; cursor:pointer; text-align:left; border-radius:8px; min-height:44px; }
.sheet-tag-btn:hover { background:rgba(255,255,255,0.06); }
.sheet-custom-tag-btn { width:100%; display:flex; align-items:center; gap:8px; padding:10px; font-size:13px; color:#4D94FF; background:none; border:none; cursor:pointer; text-align:left; border-top:1px solid rgba(255,255,255,0.05); margin-top:8px; min-height:44px; }
/* Transitions */
.fade-enter-active,.fade-leave-active{transition:opacity 0.3s;}
.fade-enter-from,.fade-leave-to{opacity:0;}
.backdrop-enter-active,.backdrop-leave-active{transition:opacity 0.25s;}
.backdrop-enter-from,.backdrop-leave-to{opacity:0;}
.sheet-up-enter-active,.sheet-up-leave-active{transition:transform 0.35s cubic-bezier(0.32,0.72,0,1);}
.sheet-up-enter-from,.sheet-up-leave-to{transform:translateY(100%);}
</style>
